import React, { Fragment, useEffect, useRef } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { equals, find, includes, not, propEq } from "ramda";
import { Link as RouterLink } from "react-router-dom";

import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import TablePagination from "@mui/material/TablePagination";

import EditIcon from "@mui/icons-material/Edit";
import CreateCurationIcon from "@mui/icons-material/PlaylistAdd";
import { notBlank } from "../util/ext";
import { useMediaTable } from "../creator-application/common/use-mediaTable";
import Tooltip from "@mui/material/Tooltip";
import { Box, Link, Typography } from "@mui/material";
import { DateTime } from "luxon";
import TableSortLabel from "@mui/material/TableSortLabel";
import ConfirmButton from "../components/ConfirmButton";
import { locality } from "../util/organizationUtil";

const useStyles = makeStyles(theme => ({
  root: {
    width: "calc(100% + 12px)",
    marginLeft: "-6px",
    borderRadius: "20px",
  },

  link: {
    lineHeight: 1.43,
    width: 300,
    color: theme.palette.primary.brandPrimary,
    flexDirection: "column",
    alignItems: "flex-start",
  },

  icon: {
    color: theme.palette.primary.brandPrimary,
  },

  cell: {
    minWidth: 80,
  },

  cardCell: {
    maxWidth: 300,
    minWidth: 300,
  },

  typeCell: {
    textTransform: "capitalize",
  },

  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },

  pagination: {
    color: theme.palette.primary.darker,
    backgroundColor: "white",
    borderRadius: "20px",
  },
}));

const formatPrice = price => {
  return price.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
};

const SubscriptionTable = props => {
  const {
    subscriptionList,
    organizationList,
    organizationId,
    page,
    onPageChange,
    onSelectChange,
    orderBy = "type",
    order = "asc",
    onSortChange,
    onScrollVisible,
    onDeleteClicked,
    onEditClicked,
    onCreateCurationClick,
  } = props;

  const classes = useStyles();
  const {
    state,
    handleRequestSort,
    handleChangePage,
    handleRowClick,
  } = useMediaTable(
    {
      onSortChange,
      onPageChange,
      onSelectChange,
    },
    {
      sort: {
        property: orderBy,
        order,
      },
      page,
      selected: [],
      editId: null,
    },
  );

  const findOrganizationName = id => {
    const org = find(propEq("organizationId", id), organizationList);
    return notBlank(org) ? org.organizationName : "...";
  };

  const findLocality = id => {
    const org = find(propEq("organizationId", id), organizationList);
    return notBlank(org) && notBlank(org.location) ? locality(org) : "-";
  };

  //-- Track if the scroll bar is visible
  const tableContainerEl = useRef(null);
  useEffect(() => {
    if (notBlank(tableContainerEl)) {
      const element = tableContainerEl.current;
      const scrollVisible = element.scrollHeight > element.clientHeight;
      onScrollVisible(scrollVisible);
    }
  }, [onScrollVisible]);
  return (
    <Paper elevation={0} className={classes.root}>
      <TableContainer ref={tableContainerEl}>
        <Table stickyHeader aria-label="Subscriptions">
          <TableHead>
            <TableRow>
              <TableCell key="organization" align="left">
                Organization
              </TableCell>

              <TableCell key="module" align="left">
                <TableSortLabel
                  active={orderBy === "module.displayName"}
                  direction={order}
                  onClick={() => handleRequestSort("module.displayName")}
                >
                  Module
                  {orderBy === "module.displayName" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell key="type" align="left" className={classes.cell}>
                <TableSortLabel
                  active={orderBy === "subscription.type"}
                  direction={order}
                  onClick={() => handleRequestSort("subscription.type")}
                >
                  Type
                  {orderBy === "subscription.type" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell
                key="sales-price"
                align="right"
                className={classes.cell}
              >
                <TableSortLabel
                  active={orderBy === "subscription.salesPrice"}
                  direction={order}
                  onClick={() => handleRequestSort("subscription.salesPrice")}
                >
                  Sales Price
                  {orderBy === "subscription.salesPrice" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell
                key="perpetual"
                align="center"
                className={classes.cell}
              >
                Perpetual
              </TableCell>

              <TableCell key="indirect" align="center" className={classes.cell}>
                Indirect
              </TableCell>

              <TableCell
                key="start-date"
                align="right"
                className={classes.cell}
              >
                <TableSortLabel
                  active={orderBy === "subscription.startDate"}
                  direction={order}
                  onClick={() => handleRequestSort("subscription.startDate")}
                >
                  Start Date
                  {orderBy === "subscription.startDate" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell key="end-date" align="right" className={classes.cell}>
                <TableSortLabel
                  active={orderBy === "subscription.endDate"}
                  direction={order}
                  onClick={() => handleRequestSort("subscription.endDate")}
                >
                  End Date
                  {orderBy === "subscription.endDate" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell key="actions" align="center" />
            </TableRow>
          </TableHead>

          <TableBody>
            {subscriptionList.map(sub => (
              <TableRow
                onClick={() => handleRowClick(sub)}
                hover
                tabIndex={-1}
                key={sub.id}
                selected={includes(sub.id, state.selected)}
              >
                <TableCell
                  key={"organization-name:" + sub.id}
                  align="left"
                  className={classes.cardCell}
                >
                  <Tooltip title={findOrganizationName(sub.organizationId)}>
                    <Link
                      component={RouterLink}
                      to={{
                        pathname: `/manage-organizations/organizations/${sub.organizationId}`,
                      }}
                      state={{
                        label: "Back to Subscriptions",
                        previousLocation: "/manage-subscriptions",
                      }}
                      aria-label={`Go to ${findOrganizationName(
                        sub.organizationId,
                      )}`}
                      classes={{
                        root: classes.link,
                      }}
                    >
                      <Typography noWrap style={{ fontWeight: 700 }}>
                        {findOrganizationName(sub.organizationId)}
                      </Typography>
                      <Box fontSize="1.2rem" fontWeight="700">
                        {findLocality(sub.organizationId)}
                      </Box>
                      <Box fontSize="1.2rem" fontWeight="700">
                        ({sub.organizationId})
                      </Box>
                    </Link>
                  </Tooltip>
                </TableCell>

                <TableCell
                  key={"module:" + sub.id}
                  align="left"
                  className={classes.cardCell}
                >
                  <Tooltip title={sub.name}>
                    <Box>
                      <Typography noWrap>{sub.name}</Typography>
                      <Box fontSize="1.2rem">({sub.mediaId})</Box>
                    </Box>
                  </Tooltip>
                </TableCell>

                <TableCell
                  key={"type:" + sub.id}
                  align="left"
                  className={classes.cell}
                >
                  {sub.type}
                </TableCell>

                <TableCell
                  key={"sales-price:" + sub.id}
                  align="right"
                  className={classes.cell}
                >
                  {formatPrice(sub.salesPrice)}
                </TableCell>

                <TableCell
                  key={"perpetual:" + sub.id}
                  align="center"
                  className={classes.cell}
                >
                  {sub.perpetual ? "Yes" : "No"}
                </TableCell>

                <TableCell
                  key={"indirect:" + sub.id}
                  align="center"
                  className={classes.cell}
                >
                  {notBlank(organizationId) &&
                  not(equals(sub.organizationId, organizationId))
                    ? "Yes"
                    : "No"}
                </TableCell>

                <TableCell
                  key={"start-date:" + sub.id}
                  align="right"
                  className={classes.cell}
                >
                  {DateTime.fromISO(sub.startDate).toUTC().toLocaleString()}
                </TableCell>

                <TableCell
                  key={"end-date:" + sub.id}
                  align="right"
                  className={classes.cell}
                >
                  {DateTime.fromISO(sub.endDate).toUTC().toLocaleString()}
                </TableCell>

                <TableCell key={"actions:" + sub.id} align="center">
                  <Box
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="center"
                  >
                    {equals(sub.organizationId, organizationId) && (
                      <Fragment>
                        <Tooltip title="Edit">
                          <IconButton
                            classes={{ label: classes.icon }}
                            onClick={() => onEditClicked(sub)}
                            size="large"
                          >
                            <EditIcon fontSize="large" />
                          </IconButton>
                        </Tooltip>

                        <ConfirmButton
                          handleDelete={() => onDeleteClicked(sub)}
                          dialogTitle="Remove Subscription"
                          dialogBody="Are you sure you want to remove this subscription?"
                        />
                      </Fragment>
                    )}

                    <Tooltip title="Create Curation">
                      <IconButton
                        classes={{ label: classes.icon }}
                        onClick={() => onCreateCurationClick(sub)}
                        size="large"
                      >
                        <CreateCurationIcon fontSize="large" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        classes={{
          root: classes.pagination,
        }}
        rowsPerPageOptions={[10]}
        component="div"
        count={-1}
        rowsPerPage={10}
        page={page}
        onPageChange={(event, page) => handleChangePage(page)}
        nextIconButtonProps={{
          disabled: subscriptionList.length < 10,
        }}
        labelDisplayedRows={({ page }) => `Page ${page + 1}`}
      />
    </Paper>
  );
};

export default SubscriptionTable;
